import { Component } from '@theme/component';

class SecurHeader extends Component {
  #controller;
  #observer;
  #measureFrame;
  #previousFocus = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('toggle', this.#handleToggle, { signal, capture: true });
    document.addEventListener('click', this.#handleOutsideClick, { signal });
    document.addEventListener('focusin', this.#handleOutsideClick, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    window.addEventListener('resize', this.#handleResize, { signal, passive: true });
    window.addEventListener('scroll', this.#scheduleMeasure, { signal, passive: true, capture: true });
    window.addEventListener('pageshow', this.#scheduleMeasure, { signal });
    for (const dialog of this.querySelectorAll('dialog[data-sc-modal]')) {
      dialog.addEventListener('close', this.#handleDialogClose, { signal });
      dialog.addEventListener('click', this.#handleBackdropClick, { signal });
    }
    this.#observer = new ResizeObserver(this.#scheduleMeasure);
    this.#observer.observe(this);
    const group = this.closest('#header-group');
    if (group) this.#observer.observe(group);
    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    if (this.#measureFrame) cancelAnimationFrame(this.#measureFrame);
    for (const dialog of this.querySelectorAll('dialog[open]')) dialog.close();
  }

  #scheduleMeasure = () => {
    if (this.#measureFrame) cancelAnimationFrame(this.#measureFrame);
    this.#measureFrame = requestAnimationFrame(() => {
      this.#measureFrame = undefined;
      this.#measure();
    });
  };

  #measure = () => {
    if (!this.isConnected) return;
    const headerHeight = this.getBoundingClientRect().height;
    const rowHeight = this.querySelector('.sc-header__main')?.getBoundingClientRect().height || headerHeight;
    const announcementHeight = this.querySelector('.sc-header__announcement')?.getBoundingClientRect().height || 0;
    const group = this.closest('#header-group');
    const groupRect = group?.getBoundingClientRect();
    const groupHeight = groupRect?.height || headerHeight;
    const sticky = this.dataset.sticky === 'true';
    const pageWrapper = this.closest('.page-wrapper');
    const scrollOffset = Math.max(window.scrollY, document.scrollingElement?.scrollTop || 0, pageWrapper?.scrollTop || 0);
    const visibleHeight = sticky && groupRect ? Math.min(groupHeight, Math.max(rowHeight, groupRect.bottom)) : groupHeight;
    if (group) group.style.setProperty('--sc-announcement-height', `${announcementHeight}px`);
    this.dataset.scrolled = String(sticky && scrollOffset > announcementHeight + 1);
    document.body.style.setProperty('--header-height', `${Math.min(headerHeight, visibleHeight)}px`);
    document.body.style.setProperty('--header-group-height', `${visibleHeight}px`);
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    this.style.setProperty('--top-row-height', `${rowHeight}px`);
  };

  #handleResize = () => {
    if (window.matchMedia('(min-width: 990px)').matches) {
      for (const dialog of this.querySelectorAll('dialog[data-sc-mobile-modal][open]')) dialog.close();
    }
    this.#scheduleMeasure();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const closeButton = event.target.closest('[data-sc-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      return;
    }
    const trigger = event.target.closest('[data-sc-dialog]');
    if (!trigger || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const dialog = document.getElementById(trigger.dataset.scDialog);
    if (!(dialog instanceof HTMLDialogElement) || !this.contains(dialog) || !dialog.showModal) return;
    event.preventDefault();
    this.#closeDesktopMenus();
    for (const openDialog of this.querySelectorAll('dialog[open]')) {
      if (openDialog !== dialog) openDialog.close();
    }
    if (dialog.open) return;
    const externalTrigger = [...this.querySelectorAll('[data-sc-dialog]')].find(
      (candidate) => candidate.dataset.scDialog === dialog.id && !candidate.closest('dialog')
    );
    this.#previousFocus.set(dialog, trigger.closest('dialog') ? externalTrigger || trigger : trigger);
    dialog.showModal();
    this.#setExpanded(dialog.id, true);
  };

  #handleDialogClose = (event) => {
    const dialog = event.currentTarget;
    this.#setExpanded(dialog.id, false);
    const trigger = this.#previousFocus.get(dialog);
    this.#previousFocus.delete(dialog);
    if (trigger instanceof HTMLElement && trigger.isConnected && !document.querySelector('dialog[open]')) {
      trigger.focus({ preventScroll: true });
    }
  };

  #handleBackdropClick = (event) => {
    const dialog = event.currentTarget;
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  };

  #handleToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.hasAttribute('data-sc-desktop-menu')) return;
    this.#closeDesktopMenus(details);
  };

  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Node)) return;
    for (const details of this.querySelectorAll('details[data-sc-desktop-menu][open]')) {
      if (!details.contains(event.target)) details.open = false;
    }
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || this.querySelector('dialog[open]')) return;
    const openDetails = this.querySelector('details[data-sc-desktop-menu][open]');
    if (!openDetails) return;
    openDetails.open = false;
    openDetails.querySelector('summary')?.focus();
    event.preventDefault();
  };

  #closeDesktopMenus(except) {
    for (const details of this.querySelectorAll('details[data-sc-desktop-menu][open]')) {
      if (details !== except) details.open = false;
    }
  }

  #setExpanded(id, expanded) {
    for (const trigger of this.querySelectorAll('[data-sc-dialog]')) {
      if (trigger.dataset.scDialog === id) trigger.setAttribute('aria-expanded', String(expanded));
    }
  }
}

if (!customElements.get('secur-header')) customElements.define('secur-header', SecurHeader);
